import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { settingsApi } from '../../api/settings'
import type { TenantSettings } from '../../api/types'
import { Alert } from '../../components/Alert'
import { Modal } from '../../components/Modal'
import { LoadingState } from '../../components/States'

type AutoCreateKey =
  | 'autoCreateCustomers'
  | 'autoCreateVendors'
  | 'autoCreateTaxAgencies'
  | 'autoCreateItems'
  | 'autoCreateAccounts'

/* Matches the list on the Preferences page: the account types plausibly named by
   a spreadsheet. Balance-sheet types are left out on purpose — those carry
   opening balances and posting consequences a bulk import should not decide. */
const ACCOUNT_TYPES = ['Expense', 'Income', 'Cost of Goods Sold', 'Other Expense', 'Other Income']

/**
 * The prototype's "Import settings" modal. These are tenant defaults in the
 * contract (`GET|PATCH /api/settings`), so saving here changes them globally.
 */
export function ImportSettingsModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  /** Called after Apply succeeds — the caller should invalidate anything derived from the old settings. */
  onSaved?: () => void
}) {
  const [draft, setDraft] = useState<TenantSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    // Raising the spinner synchronously is the point: opening the modal must not
    // show a stale draft while the GET is in flight.
    // oxlint-disable-next-line react/set-state-in-effect
    setLoading(true)
    setError(null)
    settingsApi
      .get()
      .then(setDraft)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : 'Could not load settings.'),
      )
      .finally(() => setLoading(false))
  }, [open])

  const set = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current))

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setError(null)
    try {
      await settingsApi.update(draft)
      onSaved?.()
      onClose()
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save settings.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title="Import settings"
      onClose={onClose}
      footer={
        <>
          <span className="fs12 faint">Saved as tenant defaults for every future job.</span>
          <span className="spacer" />
          <button className="btn btn-g" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-p" type="button" disabled={!draft || saving} onClick={save}>
            {saving ? 'Saving…' : 'Apply'}
          </button>
        </>
      }
    >
      {error ? <Alert tone="err" title="Settings">{error}</Alert> : null}
      {loading || !draft ? (
        <LoadingState label="Loading settings…" />
      ) : (
        <>
          <p className="fs13 muted" style={{ margin: '0 0 16px' }}>
            These apply to every import in this workspace.
          </p>

          <details className="acc" open>
            <summary>General</summary>
            <div className="acc-b">
              <div className="grid2" style={{ gap: 14 }}>
                <div className="fgrp" style={{ margin: 0 }}>
                  <label className="fl" htmlFor="dateFormat">Date format in the file</label>
                  <select
                    className="inp"
                    id="dateFormat"
                    value={draft.dateFormat}
                    onChange={(e) => set('dateFormat', e.target.value)}
                  >
                    <option value="MM/dd/yyyy">MM/DD/YYYY</option>
                    <option value="dd/MM/yyyy">DD/MM/YYYY</option>
                    <option value="yyyy-MM-dd">YYYY-MM-DD</option>
                    <option value="auto">Detect per column</option>
                  </select>
                </div>
                <div className="fgrp" style={{ margin: 0 }}>
                  <label className="fl" htmlFor="decimalSeparator">Decimal separator</label>
                  <select
                    className="inp"
                    id="decimalSeparator"
                    value={draft.decimalSeparator}
                    onChange={(e) => set('decimalSeparator', e.target.value)}
                  >
                    <option value=".">Period (1,234.56)</option>
                    <option value=",">Comma (1.234,56)</option>
                  </select>
                </div>
                <div className="fgrp" style={{ margin: 0 }}>
                  <label className="fl" htmlFor="batchSize">Batch size</label>
                  <select
                    className="inp"
                    id="batchSize"
                    value={draft.batchSize}
                    onChange={(e) => set('batchSize', Number(e.target.value))}
                  >
                    <option value={50}>50 rows (recommended)</option>
                    <option value={30}>30 rows</option>
                    <option value={10}>10 rows</option>
                  </select>
                </div>
              </div>
              <label className="chk" style={{ marginTop: 12 }}>
                <input
                  type="checkbox"
                  checked={draft.trimWhitespace}
                  onChange={(e) => set('trimWhitespace', e.target.checked)}
                />
                <span>Trim leading and trailing spaces from every cell</span>
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={draft.firstRowIsHeader}
                  onChange={(e) => set('firstRowIsHeader', e.target.checked)}
                />
                <span>Treat the first row as a header</span>
              </label>
            </div>
          </details>

          <details className="acc">
            <summary>Validation</summary>
            <div className="acc-b">
              <label className="chk">
                <input
                  type="checkbox"
                  checked={draft.stopOnErrorRatePercent !== null}
                  onChange={(e) => set('stopOnErrorRatePercent', e.target.checked ? 10 : null)}
                />
                <span>
                  Stop the job if more than <b>{draft.stopOnErrorRatePercent ?? 10}%</b> of rows fail
                </span>
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={draft.skipDuplicateDocNumbers}
                  onChange={(e) => set('skipDuplicateDocNumbers', e.target.checked)}
                />
                <span>Skip rows whose document number already exists in QuickBooks</span>
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={draft.skipInFileDuplicates}
                  onChange={(e) => set('skipInFileDuplicates', e.target.checked)}
                />
                <span>Skip rows duplicated inside the file itself</span>
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={draft.allowNegativeQuantities}
                  onChange={(e) => set('allowNegativeQuantities', e.target.checked)}
                />
                <span>Allow negative quantities</span>
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={draft.allowClosedPeriodDates}
                  onChange={(e) => set('allowClosedPeriodDates', e.target.checked)}
                />
                <span>Allow transaction dates in a closed period</span>
              </label>
              <div className="fgrp" style={{ margin: '14px 0 0' }}>
                <label className="fl" htmlFor="amountTolerance">
                  Amount tolerance when checking line totals against the header
                </label>
                <input
                  className="inp mono"
                  id="amountTolerance"
                  style={{ maxWidth: 140 }}
                  value={draft.amountTolerance}
                  onChange={(e) => set('amountTolerance', Number(e.target.value) || 0)}
                />
              </div>
            </div>
          </details>

          <details className="acc">
            <summary>Matching new records</summary>
            <div className="acc-b">
              <p className="fs13 muted" style={{ margin: '0 0 12px' }}>
                What to do when a name in the file does not exist in QuickBooks.
              </p>
              <div className="grid2" style={{ gap: 14 }}>
                {(
                  [
                    ['autoCreateCustomers', 'Customers'],
                    ['autoCreateVendors', 'Vendors'],
                    ['autoCreateItems', 'Products / services'],
                    ['autoCreateAccounts', 'Accounts'],
                    ['autoCreateTaxAgencies', 'Tax agencies'],
                  ] as Array<[AutoCreateKey, string]>
                ).map(([key, label]) => (
                  <div className="fgrp" style={{ margin: 0 }} key={key}>
                    <label className="fl" htmlFor={key}>
                      {label}
                    </label>
                    <select
                      className="inp"
                      id={key}
                      value={draft[key] ? 'create' : 'fail'}
                      onChange={(e) => set(key, e.target.value === 'create')}
                    >
                      <option value="create">Create automatically</option>
                      <option value="fail">Fail the row</option>
                    </select>
                  </div>
                ))}
              </div>

              {/* Without a type, QuickBooks rejects every account this modal
                  offers to create — and because a name-only reference is accepted
                  for customers but not for accounts, the failure would land at
                  write time looking like a mapping error. */}
              <div className="fgrp" style={{ margin: '14px 0 0' }}>
                <label className="fl" htmlFor="defaultAccountType">
                  Account type for accounts created automatically
                </label>
                <select
                  className="inp"
                  id="defaultAccountType"
                  value={draft.defaultAccountType ?? ''}
                  onChange={(e) => set('defaultAccountType', e.target.value || null)}
                >
                  <option value="">Do not create accounts</option>
                  {ACCOUNT_TYPES.map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </details>
        </>
      )}
    </Modal>
  )
}
